// Copyright (c) Bentley Systems
import {
  Anchor,
  Button,
  IconButton,
  MenuItem,
  ProgressRadial,
  Tile,
  ToggleSwitch,
} from "@itwin/itwinui-react";
import { useNavigate } from "react-router-dom";
import { SvgProgressBackward, SvgSettings } from "@itwin/itwinui-icons-react";
import { selectAccessToken } from "../app/authorization-slice";
import { getstyleFromString } from "../Helpers/helper-functions";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { useCallback, useMemo, useState } from "react";
import { ActionProps } from "./ActionProps";
import IconByName from "../Icons/IconByName";
import { setConfirmation } from "../app/confirmation-slice";
import {
  selectFormChanged,
  selectFormValid,
  selectNewFormAllValidNoChange,
} from "../app/form-changed-slice";
import DOMPurify from "dompurify";
import {
  ACTION,
  STATUS,
  useToastNotificationService,
} from "../Helpers/ToastNotificationService";
import { setIsToastError } from "../app/is-toast-error-slice";

const Action = (actionProps: ActionProps) => {
  const [isLoading, setLoading] = useState<boolean>(
    actionProps.isCalledDirectlyByUrl !== undefined &&
      actionProps.isCalledDirectlyByUrl === true
      ? true
      : false,
  );
  const accessToken = useAppSelector(selectAccessToken);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isFormChanged = useAppSelector(selectFormChanged);
  const isFormValid = useAppSelector(selectFormValid);
  const isNewFormAllValidNoChange = useAppSelector(
    selectNewFormAllValidNoChange,
  );
  const { showToast } = useToastNotificationService();

  const executeInVm = useCallback(async () => {
    await actionProps.action
      .executeAction(
        () => setLoading(true),
        () => setLoading(false),
        actionProps,
        (navigateStr: string) => {
          navigateStr = navigateStr + window.location.search;
          void navigate(navigateStr);
        },
        accessToken,
        dispatch,
      )
      .then(() => {
        if (
          (actionProps.action.toastNotification &&
            actionProps.action.toastNotification.toastNotificationTexts
              ?.length > 0) ||
          (actionProps.action.formBehavior !== undefined &&
            actionProps.action.formBehavior !== "N") ||
          actionProps.action.caption?.toLowerCase().trim() === "delete"
        ) {
          const status = STATUS.POSITIVE;
          const toastMessage =
            actionProps.action.toastNotification?.toastNotificationTexts.find(
              (t) => t.lang === "EN",
            )?.positive || "";

          let formAction = ACTION.DELETE;
          switch (actionProps.action?.formBehavior) {
            case "S":
              formAction = ACTION.CREATE;
              break;
            case "R":
              formAction = ACTION.UPDATE;
              break;
          }
          showToast({
            action: formAction,
            status,
            message: toastMessage,
          });
        }
      })
      .catch(() => {
        let formAction = ACTION.DELETE;
        switch (actionProps.action?.formBehavior) {
          case "S":
            formAction = ACTION.CREATE;
            break;
          case "R":
            formAction = ACTION.UPDATE;
            break;
        }

        const status = STATUS.NEGATIVE;
        const toastMessage =
          actionProps.action.toastNotification?.toastNotificationTexts.find(
            (t) => t.lang === "EN",
          )?.negative || "Operation was unsuccessful.";

        dispatch(
          setIsToastError({
            status,
            action: formAction,
            message: toastMessage,
          }),
        );
        setLoading(false);
      });
  }, [accessToken, actionProps, dispatch, navigate]);

  const executeAction = useCallback(
    async (props: ActionProps) => {
      if (
        props.action.hasConfirmation &&
        (props.action.isItemAction ||
          (props.action.isFormAction && isFormChanged === true))
      ) {
        dispatch(
          setConfirmation({
            isConfirmationOpen: true,
            onOkAction: executeInVm,
            caption: props.action?.confirmationText?.caption,
            text: props.action?.confirmationText?.text,
            okText: props.action?.confirmationText?.ok,
            cancelText: props.action?.confirmationText?.cancel,
          }),
        );
      } else {
        await executeInVm();
      }
    },
    [dispatch, executeInVm, isFormChanged],
  );

  const click = useCallback(async () => {
    await executeAction(actionProps);
  }, [actionProps, executeAction]);

  const isAnchor = useMemo((): boolean => {
    if (
      actionProps.action.style?.customData !== undefined &&
      actionProps.action.style?.customData !== null
    ) {
      const str = "iTwinButtonStyleType:";
      if (actionProps.action.style?.customData.indexOf(str) > -1) {
        const customStyleType = actionProps.action.style?.customData
          .substring(str.length)
          .trim();
        if (customStyleType === "anchor") {
          return true;
        }
      }
    }
    return false;
  }, [actionProps.action.style?.customData]);

  const styleType = useMemo(():
    | "borderless"
    | "default"
    | "cta"
    | "high-visibility"
    | undefined => {
    if (
      actionProps.action.style?.backgroundImageBase64Str !== undefined &&
      actionProps.action.style?.backgroundImageBase64Str !== null
    ) {
      return "borderless";
    }
    if (
      actionProps.action.style?.customData !== undefined &&
      actionProps.action.style?.customData !== null
    ) {
      const str = "iTwinButtonStyleType:";
      if (actionProps.action.style?.customData.indexOf(str) > -1) {
        const customStyleType = actionProps.action.style?.customData
          .substring(str.length)
          .trim();
        switch (customStyleType) {
          case "high-visibility":
            return "high-visibility";
          case "borderless":
            return "borderless";
          case "cta":
            return "cta";
          case "default":
            return "default";

          default:
            break;
        }
      }
    }
    return "default";
  }, [
    actionProps.action.style?.backgroundImageBase64Str,
    actionProps.action.style?.customData,
  ]);

  const style = useMemo((): import("react").CSSProperties | undefined => {
    if (
      actionProps.action.style !== undefined &&
      actionProps.action.style !== null &&
      actionProps.action.style.data !== undefined &&
      actionProps.action.style.data !== ""
    ) {
      return getstyleFromString(actionProps.action.style.data);
    }
  }, [actionProps.action.style]);

  const getSvgIcon = useCallback(
    (customData: string): React.JSX.Element | undefined => {
      let str = "itwinicon:";
      const data = customData.toLocaleLowerCase();
      if (data.indexOf(str) > -1) {
        return <IconByName name={data.substring(str.length).trim()} />;
      }
      str = "togglebutton: on";
      if (data.indexOf(str) > -1) {
        return <ToggleSwitch size="small" checked={true} readOnly />;
      }
      str = "togglebutton: off";
      if (data.indexOf(str) > -1) {
        return <ToggleSwitch size="small" checked={false} readOnly />;
      }
    },
    [],
  );

  const iconForItemAction = useMemo((): React.JSX.Element | undefined => {
    if (
      actionProps.action.style?.customData !== undefined &&
      actionProps.action.style?.customData !== null
    ) {
      return getSvgIcon(actionProps.action.style?.customData);
    } else {
      return <SvgSettings />;
    }
  }, [actionProps.action.style?.customData, getSvgIcon]);

  const getAction = useMemo(() => {
    /// header actions
    if (
      actionProps.action.isItemAction === undefined ||
      actionProps.action.isItemAction === false
    ) {
      /// form header action if has image => look like tile
      if (
        actionProps.action.isFormAction === true &&
        actionProps.action.style?.backgroundImageBase64Str !== undefined &&
        actionProps.action.style?.backgroundImageBase64Str !== null
      ) {
        return (
          <Tile.Wrapper>
            <Tile.Name>
              <Tile.NameIcon />
              <Tile.NameLabel>
                <Tile.Action onClick={click}>
                  {actionProps.action.caption}
                </Tile.Action>
              </Tile.NameLabel>
            </Tile.Name>
            <Tile.ThumbnailArea>
              <Tile.ThumbnailPicture>
                <img
                  style={{
                    height: "9.5rem",
                    width: "9.5rem",
                    marginTop: "-1.75rem",
                    marginLeft: "-1.75rem",
                    filter: actionProps.action.isGrayedOut
                      ? "grayscale(100%)"
                      : "none", // Conditionally apply grayscale filter
                    opacity: actionProps.action.isGrayedOut ? 0.5 : 1, // Conditionally adjust opacity
                  }}
                  src={getSanitizedImgSource(
                    actionProps.action.style?.backgroundImageBase64Str,
                  )}
                  alt={actionProps.action.caption}
                />
              </Tile.ThumbnailPicture>
              <Tile.BadgeContainer />
            </Tile.ThumbnailArea>
            <Tile.ContentArea />
          </Tile.Wrapper>
        );
      }
      /// elist header action => look like button
      else {
        if (actionProps.action.isBackHeaderButton) {
          return (
            <Button
              key={actionProps.action.actionId}
              styleType="borderless"
              onClick={click}
            >
              <SvgProgressBackward />
            </Button>
          );
        }
        return (
          <div key={actionProps.action.actionId} style={{ marginBottom: 2 }}>
            {isAnchor ? (
              <Anchor
                style={{ textDecorationLine: "underline" }}
                key={actionProps.action.actionId}
                onClick={click}
              >
                {actionProps.action.caption}
              </Anchor>
            ) : (
              <Button
                key={actionProps.action.actionId}
                as="button"
                onClick={click}
                styleType={styleType}
                style={style}
                disabled={setIsActionDisabled()}
              >
                <div key={actionProps.action.actionId} className="rowWrap">
                  {actionProps.action.style?.backgroundImageBase64Str !==
                    undefined &&
                    actionProps.action.style?.backgroundImageBase64Str !==
                      null && (
                      <div>
                        <img
                          src={getSanitizedImgSource(
                            actionProps.action.style?.backgroundImageBase64Str,
                          )}
                          alt={actionProps.action.caption}
                        />
                      </div>
                    )}
                  {actionProps.action.style?.customData !== undefined &&
                    actionProps.action.style?.customData !== null &&
                    getSvgIcon(actionProps.action.style?.customData)}
                  <div>{actionProps.action.caption}</div>
                  {isLoading && <ProgressRadial size="x-small" indeterminate />}
                </div>
              </Button>
            )}
          </div>
        );
      }
    } else {
      // item actions
      if (actionProps.action.isInThreeDotMenu === true) {
        // in three-dot menu
        return (
          <MenuItem
            key={actionProps.action.actionId}
            startIcon={iconForItemAction}
            onClick={() => {
              void click();
              if (actionProps.onaActionClick !== undefined) {
                actionProps.onaActionClick();
              }
            }}
          >
            {actionProps.action.caption}
          </MenuItem>
        );
      } else {
        // in table cell
        return (
          <div>
            <IconButton
              onClick={click}
              styleType="borderless"
              key={actionProps.action.actionId}
              style={style}
              label={actionProps.action.caption}
            >
              {isLoading ? (
                <ProgressRadial size="x-small" indeterminate />
              ) : (
                iconForItemAction
              )}
            </IconButton>
          </div>
        );
      }
    }

    function getSanitizedImgSource(base64str: string): string | undefined {
      const sanitizedBase64Str = DOMPurify.sanitize(base64str);
      return `data:image/jpeg;base64,${sanitizedBase64Str}`;
    }

    function setIsActionDisabled(): boolean | undefined {
      // Check if custom disabled prop is provided
      if (actionProps.disabled !== undefined) {
        return actionProps.disabled;
      }

      if (actionProps.action.isSaveAction === false) {
        return false;
      }
      if (isNewFormAllValidNoChange === true) {
        return false;
      }
      return isFormValid === false || isFormChanged === false;
    }
  }, [
    actionProps,
    click,
    isAnchor,
    styleType,
    style,
    getSvgIcon,
    isLoading,
    iconForItemAction,
    isNewFormAllValidNoChange,
    isFormValid,
    isFormChanged,
  ]);

  return <div>{getAction}</div>;
};

export default Action;
